import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import OrderApprovalView from './components/OrderApprovalView'; // Import the view component
import LinearProgress from '@mui/material/LinearProgress';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Tooltip } from '@mui/material';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { OPERATIONAL_STATUS_SECURITY_ORDER_LIST } from 'src/utils/pnd/PndConstants';
// ... (WaitTimeProgress component)
const WaitTimeProgress = ({ startTime }) => {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        const calculateElapsed = () => {
            const now = new Date();
            const start = new Date(startTime);
            const diffInSeconds = Math.floor((now - start) / 1000);
            setElapsed(diffInSeconds);
        };

        calculateElapsed();
        const interval = setInterval(calculateElapsed, 1000);
        return () => clearInterval(interval);
    }, [startTime]);

    const percentage = Math.min(100, (elapsed / 60) * 100);
    const color = elapsed > 60 ? "error" : "success";

    return (
        <div style={{ width: '100%' }}>
            <div className="d-flex justify-content-between">
                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {elapsed > 60 ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s` : `${elapsed}s`}
                </small>
            </div>
            <LinearProgress variant="determinate" value={percentage} color={color} sx={{ height: 6, borderRadius: 3 }} />
        </div>
    );
};

const OrderApprovalIndex = () => {
    const { t } = useTranslation('common');
    const history = useHistory();
    const [reactTableData, setReactTableData] = useState({ tableHeaderData: [], tableRowsData: [] });
    const [loading, setLoading] = useState(false);
    const [isNoRecordFound, setNoRecordFound] = useState(false);
    const [allOrders, setAllOrders] = useState([]);
    const [activeFilter, setActiveFilter] = useState('all');

    const handleRowClick = useCallback((row) => {
        history.push({
            pathname: '/web_app/order/approval/details',
            state: { order: row.original }
        });
    }, [history]);

    const filteredRows = useMemo(() => {
        const nonApproved = allOrders.filter(r => r.securityApprovalStatus !== 1);
        if (activeFilter === 'pending') return nonApproved.filter(r => r.securityApprovalStatus === null);
        if (activeFilter === 'declined') return nonApproved.filter(r => r.securityApprovalStatus === 2);
        return nonApproved;
    }, [allOrders, activeFilter]);

    const stats = useMemo(() => {
        const data = allOrders;
        const total = data.length;
        const pending = data.filter(r => r.securityApprovalStatus === null).length;
        const approved = data.filter(r => r.securityApprovalStatus === 1).length;
        const declined = data.filter(r => r.securityApprovalStatus === 2).length;
        return { total, pending, approved, declined };
    }, [reactTableData]);

    const columns = useMemo(
        () => [
            {
                Header: '#',
                accessor: 'index',
                disableGlobalFilter: true,
                width: 50,
                Cell: (row) => {
                    return <span>{row.row.index + 1}</span>;
                }
            },
            {
                Header: 'Action',
                accessor: 'action',
                disableSortBy: true,
                width: 100,
                Cell: ({ row }) => {
                    const status = row.original.securityApprovalStatus;
                    const isPending = status === null;
                    const isDeclined = status === 2;
                    const iconColor = isDeclined ? '#dc3545' : isPending ? '#f0a500' : '#007bff';
                    const tooltipLabel = isDeclined ? 'Declined' : isPending ? 'Pending' : 'Approved';
                    return (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <Tooltip title={tooltipLabel} arrow placement="top">
                                <VisibilityIcon
                                    style={{ color: iconColor, cursor: 'pointer', fontSize: '1.5rem', transition: 'transform 0.15s' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRowClick(row);
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                />
                            </Tooltip>
                        </div>
                    );
                }
            },
            {
                Header: 'Order No',
                accessor: 'orderNo',
                Cell: ({ value }) => <span style={{ fontWeight: 600 }}>{value}</span>
            },
            {
                Header: 'ERP No',
                accessor: 'erpNo',
                Cell: ({ value }) => <span style={{ fontWeight: 600 }}>{value}</span>
            },
            {
                Header: 'Customer Name',
                accessor: 'customerName',
            },
            {
                Header: 'Customer Code',
                accessor: 'customerCode',
            },
            {
                Header: 'Rider Name',
                accessor: 'riderName',
                width: 150,
                Cell: ({ value }) => <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{value || '-'}</span>
            },

        ],
        [handleRowClick]
    );

    useEffect(() => {
        fetchOrderList();
    }, [columns]);

    const fetchOrderList = async () => {
        setLoading(true);
        try {
            const response = await PndRestApi.GetSecurityOrderList({ operationalStatus: OPERATIONAL_STATUS_SECURITY_ORDER_LIST, page: 0, size: 100 }); // Use real API
            if (response && response.data && response.data.status === 200) {
                const paged = response.data.data || {};
                const data = paged.content || [];
                setAllOrders(data);
                if (data.length > 0) {
                    setReactTableData({ tableHeaderData: columns, tableRowsData: data });
                    setNoRecordFound(false);
                } else {
                    setReactTableData({ tableHeaderData: columns, tableRowsData: [] });
                    setNoRecordFound(true);
                }
            } else {
                setAllOrders([]);
                setReactTableData({ tableHeaderData: columns, tableRowsData: [] });
                setNoRecordFound(true);
            }
        } catch (error) {
            console.error("Error fetching security order list:", error);
            setReactTableData({ tableHeaderData: columns, tableRowsData: [] });
            setNoRecordFound(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader className="py-2">
                <h5 className="mb-0 fw-bold">Order Approval</h5>
            </CardHeader>
            <CardBody className="pt-0">
                {loading && <LinearProgress />}

                {/* Filter Tabs */}
                <div className="d-flex gap-2 mb-3 flex-wrap">
                    {[
                        { key: 'all', label: 'All', color: '#007bff' },
                        { key: 'pending', label: 'Pending', color: '#f0a500' },
                        { key: 'declined', label: 'Declined', color: '#dc3545' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveFilter(tab.key)}
                            style={{
                                padding: '4px 14px',
                                borderRadius: '20px',
                                border: `2px solid ${tab.color}`,
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                background: activeFilter === tab.key ? tab.color : '#fff',
                                color: activeFilter === tab.key ? '#fff' : tab.color,
                                transition: 'all 0.2s'
                            }}
                        >{tab.label}</button>
                    ))}
                </div>

                <OrderApprovalView
                    reactTableData={{ tableHeaderData: columns, tableRowsData: filteredRows }}
                    isNoRecordFound={filteredRows.length === 0}
                    onRowClick={handleRowClick}
                />
            </CardBody>
        </Card >
    );
};

export default OrderApprovalIndex;
